import { useId, useState, type ReactNode } from "react";
import type {
  ChannelMessageOrigin,
  ConversationMessage,
} from "@dreamverse/contracts";

type ChannelMessage = ConversationMessage & { origin: ChannelMessageOrigin };

export type ChatTimelineItem =
  | { kind: "message"; message: ConversationMessage }
  | {
      kind: "channel_encounter";
      /** 取末条事件 id：向上加载同组更早消息时 key 不变，已展开状态不会丢。 */
      id: string;
      endpointId: string;
      endpointLabel: string;
      messages: ChannelMessage[];
    };

function isChannelMessage(message: ConversationMessage): message is ChannelMessage {
  return message.origin?.kind === "channel";
}

function formatEncounterTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * 只把时间线中连续、来自同一站外端点的事件收成一组。
 * 站内消息或另一个群出现时立即结束，避免把两次不同相遇混在一起。
 */
export function groupChannelEncounters(
  messages: readonly ConversationMessage[],
): ChatTimelineItem[] {
  const items: ChatTimelineItem[] = [];

  for (const message of messages) {
    if (!isChannelMessage(message)) {
      items.push({ kind: "message", message });
      continue;
    }

    const previous = items[items.length - 1];
    if (
      previous?.kind === "channel_encounter" &&
      previous.endpointId === message.origin.endpointId
    ) {
      previous.messages.push(message);
      previous.id = `channel-encounter:${message.id}`;
      continue;
    }

    items.push({
      kind: "channel_encounter",
      id: `channel-encounter:${message.id}`,
      endpointId: message.origin.endpointId,
      endpointLabel: message.origin.endpointLabel,
      messages: [message],
    });
  }

  return items;
}

function encounterTimeRange(messages: readonly ChannelMessage[]): string {
  const first = messages[0];
  const last = messages[messages.length - 1];
  if (!first || !last) return "";
  const start = formatEncounterTime(first.createdAt);
  const end = formatEncounterTime(last.createdAt);
  if (!start) return end;
  if (!end || start === end) return start;
  return `${start}–${end}`;
}

/** 默认整组收起；展开状态只属于当前页面，不写入用户设置或消息数据。 */
export function ChannelEncounter({
  endpointLabel,
  messages,
  children,
}: {
  endpointLabel: string;
  messages: readonly ChannelMessage[];
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const timeRange = encounterTimeRange(messages);
  const label = `在 ${endpointLabel}相遇${timeRange ? ` · ${timeRange}` : ""}`;

  return (
    <section
      className={`channel-encounter${expanded ? " channel-encounter--expanded" : ""}`}
      aria-label={label}
    >
      <button
        type="button"
        className="channel-encounter__toggle"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((value) => !value)}
      >
        <span className="channel-encounter__label">{label}</span>
        <span className="channel-encounter__arrow" aria-hidden>›</span>
      </button>

      {expanded ? (
        <div id={contentId} className="channel-encounter__messages">
          {children}
        </div>
      ) : null}
    </section>
  );
}
